// ABOUTME: Loads the in-cart recommendations carousel (FEAT-CART-RECS) and keeps it anchored to the
// first cart line as the cart changes; re-renders when the cart's product set changes and reads the
// cart template's section id from /cart for a host that cannot name it.
// That runtime read is the contract for the drawer mount, which ships without a section id because
// the id is the cart template's own object id, and for any host whose named id misses on this copy.

import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartEvents } from '@theme/jf-cart-events';

/**
 * Forked from assets/product-recommendations.js. The shipped component anchors to a fixed
 * merchant-picked product, so it loads once and is done. A cart surface anchors to the first cart
 * line: removing or reordering that line changes what should be recommended, and this section
 * carries no cart-items-component, so the jf-cart operator's Section Rendering bundle never morphs
 * it. The anchor is therefore re-read from /cart.js on every cart:update and the section reloads
 * itself when it moved.
 */
/**
 * Restore source per section id, module-scoped: a core-origin morph can REPLACE the element
 * (new instance, empty private state), and the replacement must still restore instantly
 * instead of waiting for the next scroll-triggered intersection.
 * @type {Map<string, string>}
 */
const lastInjectedBySection = new Map();

/** The endpoint's maximum; the section filters the result down to its max_products. */
const FETCH_LIMIT = 10;

const SECTION_ID_STORAGE_KEY = 'jf-cart-recs-section-id';
const SECTION_ID_PATTERN = /id="shopify-section-(template--\d+__jf-cart-recs)"/;

class JfCartRecs extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Sorted product ids of the cart at the last render, so a changed set re-renders. */
  #cartProductIds = '';

  /** Whether the section-id discovery already ran to a result for this element (a failed /cart read does not count, so a transient failure retries on the next load). */
  #sectionIdRecovered = false;

  /**
   * Core cart mutations morph 'cart-drawer-section' in hydration mode; this element's
   * hydration key matches, so the injected cards get reconciled back to the empty server
   * shell (which ships data-recommendations-performed="false") and core never dispatches
   * the private cart bus event. Watch that attribute: on an external reset, re-inject the
   * last render, then re-anchor via #onCartUpdate in case the mutation moved the first line.
   */
  #restoreObserver = new MutationObserver(() => this.#restoreLastRender());

  #restoreLastRender() {
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (!lastInjectedBySection.has(this.dataset.sectionId)) return;
    requestAnimationFrame(() => {
      const html = lastInjectedBySection.get(this.dataset.sectionId);
      if (this.dataset.recommendationsPerformed === 'true' || !html) return;
      this.dataset.recommendationsPerformed = 'true';
      morphSection(this.dataset.sectionId, html, { mode: 'hydration', injectStylesheet: true });
      this.#onCartUpdate();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    document.addEventListener(CartEvents.cartUpdate, this.#onCartUpdate);
    this.#restoreObserver.observe(this, { attributes: true, attributeFilter: ['data-recommendations-performed'] });
    // A morph that replaced the previous instance connects this one in the wiped state.
    this.#restoreLastRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    document.removeEventListener(CartEvents.cartUpdate, this.#onCartUpdate);
    this.#restoreObserver.disconnect();
  }

  /**
   * The cart may have changed (a line added, removed, reordered, quantity-zeroed). The section
   * filters out products already in the cart at render time, so a changed product set re-renders
   * even when the anchor line stayed; an emptied cart leaves the last render in place rather than
   * flashing an empty rail — the cart surfaces themselves render their empty state.
   */
  #onCartUpdate = async () => {
    let anchorId;
    let productIds;
    try {
      const cart = await fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`).then((response) => response.json());
      anchorId = cart.items[0]?.product_id;
      productIds = cart.items
        .map((item) => String(item.product_id))
        .sort()
        .join(',');
    } catch (error) {
      console.error('jf-cart-recs: cart read failed', error);
      return;
    }

    if (!anchorId) return;
    if (String(anchorId) === this.dataset.productId && productIds === this.#cartProductIds) return;

    this.#cartProductIds = productIds;
    this.#cachedRecommendations = {};
    this.dataset.productId = String(anchorId);
    this.#loadRecommendations({ force: true });
  };

  /**
   * @param {{force?: boolean}} [options] - force bypasses the already-loaded guard (a cart change
   * invalidates a previous render even though it succeeded).
   */
  async #loadRecommendations({ force = false } = {}) {
    const { productId, recommendationsPerformed, intent } = this.dataset;

    if (!productId) {
      throw new Error('A product ID is required');
    }

    // Accounts for the Theme Editor loading the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true' && !force) {
      return;
    }

    // A host that cannot name the cart template's section instance mounts the rail without an
    // id: read the real one from the cart page before the first fetch, the same read a named
    // id that misses on this copy reaches through its 404.
    if (!this.dataset.sectionId && !this.#sectionIdRecovered) {
      await this.#recoverSectionId();
    }

    const { sectionId } = this.dataset;
    if (!sectionId) return;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          // A superseded fetch was aborted by a newer load; the newer load owns the outcome.
          if (result.superseded) return;
          // The Theme Editor places section elements in the DOM whose section_id is not available to
          // the Section Renderer API. In that case the error is safely ignored.
          if (Shopify.designMode) return;
          // A host that names the section instance by a constant names one theme copy's id; on
          // another copy that id 404s. Read the real id from the cart page once and retry under it.
          if (result.status === 404 && !this.#sectionIdRecovered && (await this.#recoverSectionId())) {
            this.#loadRecommendations({ force: true });
            return;
          }
          this.#handleError(new Error(`Server returned ${result.status} for ${result.url}`));
          return;
        }

        if (result.data?.trim().length) {
          lastInjectedBySection.set(sectionId, result.data);
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = new URL(this.dataset.url ?? '', window.location.origin);
    url.searchParams.set('limit', String(FETCH_LIMIT));
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId ?? '');
    url.searchParams.set('intent', intent ?? '');

    const cachedResponse = this.#cachedRecommendations[url.href];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const fetchController = new AbortController();
    this.#activeFetch = fetchController;

    try {
      const response = await fetch(url, { signal: fetchController.signal });
      if (!response.ok) {
        return { success: false, status: response.status, url: url.href };
      }

      const text = await response.text();
      this.#cachedRecommendations[url.href] = text;
      return { success: true, data: text };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, status: 0, superseded: true };
      }
      throw error;
    } finally {
      if (this.#activeFetch === fetchController) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Reads the cart template's rendered section id for this rail from the cart page and re-keys
   * this element (and the wrapper morphSection targets) to it. Cached per session. This is the
   * contract that makes the drawer rail work on any theme copy, whatever its cart.json object
   * id (cutover row #610 is optional because of it).
   * @returns {Promise<boolean>} whether a different id was adopted
   */
  async #recoverSectionId() {
    let sectionId = null;
    try {
      sectionId = sessionStorage.getItem(SECTION_ID_STORAGE_KEY);
    } catch {
      sectionId = null;
    }
    if (!sectionId) {
      try {
        const html = await fetch(Theme?.routes?.cart_url ?? '/cart').then((response) => response.text());
        sectionId = html.match(SECTION_ID_PATTERN)?.[1] ?? null;
      } catch (error) {
        console.error('jf-cart-recs: cart page read failed', error);
        return false;
      }
      if (sectionId) {
        try {
          sessionStorage.setItem(SECTION_ID_STORAGE_KEY, sectionId);
        } catch {
          // per-session convenience only
        }
      }
    }
    this.#sectionIdRecovered = true;
    if (!sectionId || sectionId === this.dataset.sectionId) return false;

    // A mount that ships without an id still carries the wrapper morphSection has to re-key.
    const wrapper = this.closest(
      this.dataset.sectionId ? `#shopify-section-${this.dataset.sectionId}` : '[id^="shopify-section-"]'
    );
    if (wrapper) wrapper.id = `shopify-section-${sectionId}`;
    this.id = `jf-cart-recs-${sectionId}`;
    this.dataset.hydrationKey = `jf-cart-recs-${sectionId}`;
    this.dataset.sectionId = sectionId;
    return true;
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    console.error('jf-cart-recs error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading cart recommendations';
  }
}

if (!customElements.get('jf-cart-recs')) {
  customElements.define('jf-cart-recs', JfCartRecs);
}
